import styled from '@emotion/styled';
import { generatePath, Link } from 'react-router';
import { Icon, Popup } from 'semantic-ui-react';
import { useIntParam, useSpeakingSessionAllowedActions } from 'hooks';
import { ROUTING_PATHS } from 'const';
import {
  SpeakingSessionRoomStructureAssignedExaminerDto,
  SpeakingSlotType,
} from 'api/planning/api';
import { useRoomStructureExaminerAllocation } from './useRoomStructureExaminerAllocation';
import { RoomStructureExaminerAllocationPopup } from './RoomStructureExaminerAllocationPopup';
import { SwaCapacityEditor } from '../structures/SwaCapacityEditor';
import { RoomClashPeriodDto } from '../structures/utils/calculateClashedPeriods';

type SlotPeriod = {
  shift: number;
  isClashed: boolean;
};

type Props = {
  editing: boolean;
  sessionRoomId?: number;
  sessionRoomStructureId?: number;
  speakingSessionDate?: string;
  structureStartTime?: string;
  structureEndTime?: string;
  speakingSlotType: SpeakingSlotType;
  assignedExaminer?: SpeakingSessionRoomStructureAssignedExaminerDto;
  clashedPeriods: RoomClashPeriodDto[];
  roomCapacity: number;
  plannedCapacity: number;
  minCapacity: number;
  onCapacityChange: (capacity: number) => void;
};

export const RoomStructureExaminerAllocation = ({
  editing,
  sessionRoomId,
  sessionRoomStructureId,
  speakingSessionDate,
  structureStartTime,
  structureEndTime,
  speakingSlotType,
  assignedExaminer,
  clashedPeriods,
  roomCapacity,
  plannedCapacity,
  minCapacity,
  onCapacityChange,
}: Props) => {
  const speakingSessionId = useIntParam('sessionId');
  const { manageSpeakingSessionRoomStructureExaminer } = ROUTING_PATHS;
  const {
    structureSlotsCount,
    periodsLeft,
    periodsRight,
    isApplicable,
    assignedExaminerWithAvailability,
  } = useRoomStructureExaminerAllocation({
    speakingSessionDate,
    structureStartTime,
    structureEndTime,
    speakingSlotType,
    assignedExaminer,
    clashedPeriods,
  });

  const isExaminerAssigned = assignedExaminerWithAvailability != undefined;
  const { canAssignExaminers } = useSpeakingSessionAllowedActions();

  const renderPeriods = (side: 'left' | 'right', periods: SlotPeriod[]) => (
    <>
      {periods.map((period, index) =>
        period.isClashed ? (
          <S.ClashedFlexItem
            key={`clashed-${side}-${period.shift}-${period.isClashed}-${index}`}
            grid={period.shift}
          />
        ) : (
          <S.FlexItem
            key={`available-${side}-${period.shift}-${period.isClashed}-${index}`}
            grid={period.shift}
          />
        )
      )}
    </>
  );

  const leftPeriods = renderPeriods('left', periodsLeft);
  const rightPeriods = renderPeriods('right', periodsRight);
  const hasStructureSlots = structureSlotsCount > 0;
  const isSwa = speakingSlotType === SpeakingSlotType.SWA;

  const notApplicableAssignment = hasStructureSlots ? (
    <S.NotApplicable grid={structureSlotsCount} />
  ) : null;

  const capacityAssignment = hasStructureSlots ? (
    <S.Span grid={structureSlotsCount}>
      <SwaCapacityEditor
        capacity={plannedCapacity}
        min={minCapacity}
        max={roomCapacity}
        editable={editing}
        onChange={onCapacityChange}
      />
    </S.Span>
  ) : null;

  const assignedExaminerContent = isExaminerAssigned ? (
    <Popup
      position="top center"
      content={
        <RoomStructureExaminerAllocationPopup
          assignedExaminerWithAvailability={assignedExaminerWithAvailability}
          structureStartTime={structureStartTime}
          structureEndTime={structureEndTime}
        />
      }
      trigger={
        <S.AssignedExaminer>
          <Icon
            name={assignedExaminerWithAvailability.availability!.icon}
            color={assignedExaminerWithAvailability.availability!.color}
          />{' '}
          {assignedExaminerWithAvailability.firstName} {assignedExaminerWithAvailability.lastName}
        </S.AssignedExaminer>
      }
    />
  ) : null;

  const displayExaminerAssignment = hasStructureSlots ? (
    <S.Span grid={structureSlotsCount}>{assignedExaminerContent}</S.Span>
  ) : null;

  const renderManageExaminerContent = () => {
    if (isExaminerAssigned) {
      return assignedExaminerContent;
    }

    return <Icon link color="blue" name="plus" />;
  };

  const manageExaminerAssignment = () => {
    if (!hasStructureSlots) {
      return null;
    }

    if (!canAssignExaminers) {
      return <S.Span grid={structureSlotsCount}>{assignedExaminerContent}</S.Span>;
    }

    return (
      <S.Link
        grid={structureSlotsCount}
        to={generatePath(manageSpeakingSessionRoomStructureExaminer.absolutePath, {
          sessionId: speakingSessionId,
        })}
        state={{
          structureStartTime,
          structureEndTime,
          sessionRoomId,
          sessionRoomStructureId,
          assignedExaminerId: assignedExaminerWithAvailability?.examinerId,
        }}
        data-testid="add-examiner-link"
      >
        {renderManageExaminerContent()}
      </S.Link>
    );
  };

  const examinerAssignment = editing ? displayExaminerAssignment : manageExaminerAssignment();

  let middleContent = notApplicableAssignment;
  if (isApplicable) {
    middleContent = examinerAssignment;
  } else if (isSwa) {
    middleContent = capacityAssignment;
  }

  return (
    <S.ExaminerAssignment>
      {leftPeriods}
      {middleContent}
      {rightPeriods}
    </S.ExaminerAssignment>
  );
};

const S = {
  Span: styled.span<{ grid: number }>`
    display: flex;
    grid-column: span ${(props) => props.grid};
    justify-content: center;
    align-items: center;
    background-color: #ffffff;
    height: 100%;
    border-left: 1px #e2e2e3 solid;
    border-right: 1px #e2e2e3 solid;
    min-width: 0;
  `,
  NotApplicable: styled.span<{ grid: number }>`
    display: flex;
    grid-column: span ${(props) => props.grid};
    justify-content: center;
    align-items: center;
    background-color: #f3f4f5;
    height: 100%;
    min-width: 0;
  `,
  Link: styled(Link)<{ grid: number }>`
    display: flex;
    grid-column: span ${(props) => props.grid};
    justify-content: center;
    align-items: center;
    background-color: #ffffff;
    height: 100%;
    min-width: 0;
  `,
  FlexItem: styled.div<{ grid: number }>`
    grid-column: span ${(props) => props.grid};
    display: flex;
    min-width: 0;
  `,
  ClashedFlexItem: styled.div<{ grid: number }>`
    grid-column: span ${(props) => props.grid};
    display: flex;
    min-width: 0;
    background-color: #d5d8dd;
    height: 100%;
  `,
  AssignedExaminer: styled.span`
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  `,
  ExaminerAssignment: styled.div`
    grid-column: 1 / -1;
    background-color: #f3f4f5;
    display: grid;
    grid-template-columns: repeat(72, 1fr);
    justify-content: center;
    align-items: center;
  `,
};
